/**
 * Runtime environment detection for WebView, Capacitor, and constrained browsers.
 * Prevents ReferenceError when APIs like Notification are undefined.
 */

const UA = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';

/**
 * WebView / in-app browsers (Android: "; wv)" or Android + Version/x; iOS: no Safari token; embedded apps).
 * Aligns with common checks: /wv|Android.*Version/ and Capacitor/Flutter bridges.
 */
export function isLikelyInAppWebView(): boolean {
  if (typeof window === 'undefined') return false;
  const w = window as Window & {
    flutter_inappwebview?: unknown;
    Capacitor?: { isNativePlatform?: () => boolean };
  };
  if (w.flutter_inappwebview != null) return true;
  // @capacitor/core يعرّف window.Capacitor حتى في المتصفح — لا نعتبره WebView إلا على التطبيق الأصلي
  if (
    w.Capacitor != null &&
    typeof w.Capacitor.isNativePlatform === 'function' &&
    w.Capacitor.isNativePlatform()
  ) {
    return true;
  }
  if (/; wv\)/i.test(UA) || /\bwv\b/i.test(UA)) return true;
  if (/Android/i.test(UA) && /Version\/\d/i.test(UA)) return true;
  // iOS UIWebView/WKWebView in apps often lacks "Safari/" in the same way as full Safari
  if (/iPhone|iPad|iPod/i.test(UA) && !/Safari\//i.test(UA) && /AppleWebKit/i.test(UA)) return true;
  return false;
}

export function hasNotificationApi(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window && typeof Notification !== 'undefined';
}

/** Desktop-style Notification API (avoid WKWebView / embedded browsers that crash or lack full support) */
export function shouldUseDesktopNotifications(): boolean {
  if (!hasNotificationApi()) return false;
  if (isLikelyInAppWebView()) return false;
  try {
    if (/iPhone|iPad|iPod/i.test(UA)) return false;
  } catch {
    /* ignore */
  }
  return true;
}

export function supportsNotificationActions(): boolean {
  if (!hasNotificationApi()) return false;
  return !/iPhone|iPad|iPod/i.test(UA);
}

/** vibrate in NotificationOptions is not reliable on iOS and can throw in strict WebViews */
export function supportsNotificationVibrate(): boolean {
  if (!hasNotificationApi()) return false;
  if (/iPhone|iPad|iPod/i.test(UA)) return false;
  if (isLikelyInAppWebView()) return false;
  return true;
}

/**
 * Returns true when the web content is running inside a native mobile wrapper
 * (Capacitor native build or a custom WebView with `?nativeApp=1` flag).
 *
 * When true the React app should hide its own Header, Footer, and MobileTabBar
 * because the native wrapper provides its own navigation chrome.
 */
export function isNativeApp(): boolean {
  if (typeof window === 'undefined') return false;
  // 1. Capacitor native platform detection (Capacitor sets window.Capacitor)
  const w = window as Window & {
    Capacitor?: { isNativePlatform?: () => boolean };
  };
  if (
    w.Capacitor != null &&
    typeof w.Capacitor.isNativePlatform === 'function' &&
    w.Capacitor.isNativePlatform()
  ) {
    return true;
  }
  // 2. Query-param flag set by the native wrapper's URL (e.g. loadUrl("...?nativeApp=1"))
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('nativeApp') === '1') return true;
  } catch {
    /* ignore */
  }
  // 3. Custom User-Agent token injected by the wrapper (e.g. "OpenAuction24-Native")
  if (/OpenAuction24-Native/i.test(UA)) return true;
  // 4. Smart WebView Detection (Standard Android/iOS WebViews)
  if (isLikelyInAppWebView()) return true;
  
  return false;
}

export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}
